import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Button, TextField, IconButton, Chip, CircularProgress, Skeleton, Popover } from '@mui/material';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Clear from '@mui/icons-material/Clear';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PostCard, { PostData } from './PostCard';
import PostComposer from './PostComposer';
import GameSelectDialog from '../dialogs/GameSelectDialog';
import { getPostsByUser, getUserPosts } from '../../services/api';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';

interface PostFeedProps {
  username: string;
  isMyPage: boolean;
  avatar?: string | null;
  displayName?: string;
  autoFocusComposer?: boolean;
}

interface GameFilter {
  game_id: number;
  name: string;
}

const PostLoadingPlaceholder = () => (
  <Box>
    {[...Array(3)].map((_, index) => (
      <Box key={index} sx={{ p: 2, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
            <Skeleton variant="text" width="20%" height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
          </Box>
        </Box>
        <Skeleton variant="text" width="90%" height={18} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
        <Skeleton variant="text" width="70%" height={18} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
      </Box>
    ))}
  </Box>
);

const PostFeed: React.FC<PostFeedProps> = ({ username, isMyPage, avatar, displayName, autoFocusComposer }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // URLパラメータからフィルター初期値を取得
  const initialGameId = searchParams.get('filter_game_id');
  const initialGameName = searchParams.get('filter_game_name');
  const initialDate = searchParams.get('filter_date');

  // フィルター
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [gameFilter, setGameFilter] = useState<GameFilter | null>(
    initialGameId && initialGameName
      ? { game_id: parseInt(initialGameId), name: initialGameName }
      : null
  );
  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState(initialDate || '');
  const [dateTo, setDateTo] = useState(initialDate || '');
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);

  // フィルターURLパラメータをクリーンアップ（初期化後に消す）
  const cleanedRef = useRef(false);
  useEffect(() => {
    if (!cleanedRef.current && (initialGameId || initialDate)) {
      cleanedRef.current = true;
      const params = new URLSearchParams(searchParams);
      params.delete('filter_game_id');
      params.delete('filter_game_name');
      params.delete('filter_date');
      setSearchParams(params, { replace: true });
    }
  }, []);

  const hasFilters = !!searchQuery || !!gameFilter || !!dateFrom || !!dateTo;

  // URLから現在のページを取得
  const page = parseInt(searchParams.get('post_page') || '1');
  const tab = searchParams.get('tab') || 'posts';

  // React Query でポスト取得
  const stableKey = isMyPage ? 'me' : username;
  const { data: postsData, isLoading, isFetching } = useQuery({
    queryKey: ['posts', stableKey, page, searchQuery, gameFilter?.game_id, dateFrom, dateTo],
    queryFn: async () => {
      const filterParams = {
        ...(searchQuery && { search: searchQuery }),
        ...(gameFilter && { game_id: gameFilter.game_id }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };
      return isMyPage
        ? getUserPosts(page, filterParams)
        : getPostsByUser(username, page, filterParams);
    },
    enabled: !!username,
    staleTime: isMyPage ? 10000 : 30000,
  });

  const posts: PostData[] = postsData?.results || [];
  const hasNext = !!postsData?.next;
  const loading = isLoading && !postsData;

  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      setSearchQuery(query.trim());
      // フィルター変更時はページ1にリセット
      handlePageChange(1);
    }, 500),
    []
  );

  const handleClearFilter = () => {
    setSearchInput('');
    setSearchQuery('');
    setGameFilter(null);
    setDateFrom('');
    setDateTo('');
    handlePageChange(1);
  };

  const handlePostCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['posts', stableKey] });
  };

  const handleDelete = (postId: number) => {
    queryClient.invalidateQueries({ queryKey: ['posts', stableKey] });
  };

  const handleReactionUpdate = (postId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => {
    queryClient.setQueryData(
      ['posts', stableKey, page, searchQuery, gameFilter?.game_id, dateFrom, dateTo],
      (old: any) => {
        if (!old) return old;
        return {
          ...old,
          results: old.results.map((p: PostData) =>
            p.id === postId
              ? { ...p, reaction_counts: newReactions, user_reaction: newUserReaction }
              : p
          ),
        };
      }
    );
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('post_page', newPage.toString());
    params.set('tab', tab);
    setSearchParams(params);
  };

  return (
    <Box>
      {/* 投稿コンポーザー（マイページのみ） */}
      {isMyPage && (
        <PostComposer
          avatar={avatar}
          displayName={displayName}
          onPostCreated={handlePostCreated}
          autoFocus={autoFocusComposer}
        />
      )}

      {/* フィルター */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: isMyPage ? 2 : 0, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* フィルターChip / ボタン群 */}
          {gameFilter ? (
            <Chip
              icon={<SportsEsportsIcon sx={{ fontSize: '0.85rem !important' }} />}
              label={gameFilter.name}
              size="small"
              onDelete={() => {
                setGameFilter(null);
                handlePageChange(1);
              }}
              deleteIcon={<CloseIcon sx={{ fontSize: '0.8rem !important' }} />}
              sx={{
                height: 28,
                fontSize: '0.75rem',
                maxWidth: { xs: 160, sm: 'none' },
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            />
          ) : (
            <Button
              size="small"
              startIcon={<SportsEsportsIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setGameDialogOpen(true)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 12,
                textTransform: 'none',
                fontSize: '0.7rem',
                color: 'text.primary',
                px: 1.5,
                py: 0.5,
                lineHeight: 1,
                minHeight: 0,
                whiteSpace: 'nowrap',
              }}
            >
              ゲームフィルター
            </Button>
          )}

          {(dateFrom || dateTo) && (
            <Chip
              icon={<CalendarMonthIcon sx={{ fontSize: '0.85rem !important' }} />}
              label={dateFrom && dateTo ? `${dateFrom} ~ ${dateTo}` : dateFrom ? `${dateFrom} ~` : `~ ${dateTo}`}
              size="small"
              onDelete={() => {
                setDateFrom('');
                setDateTo('');
                handlePageChange(1);
              }}
              deleteIcon={<CloseIcon sx={{ fontSize: '0.8rem !important' }} />}
              sx={{
                height: 28,
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            />
          )}

          {/* アイコン群（右端固定） */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {!(dateFrom || dateTo) && (
              <IconButton
                size="small"
                onClick={(e) => {
                  setTempDateFrom(dateFrom);
                  setTempDateTo(dateTo);
                  setDateAnchor(e.currentTarget);
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}

            {searchOpen ? (
              <TextField
                autoFocus
                placeholder="Search..."
                variant="standard"
                size="small"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                onBlur={() => {
                  if (!searchInput) setSearchOpen(false);
                }}
                sx={{
                  width: { xs: 120, sm: 180 },
                  '& .MuiInputBase-input': {
                    fontSize: '0.85rem',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 0.5,
                  },
                  '& .MuiInputBase-root:before': {
                    borderBottomColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '& .MuiInputBase-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: 'rgba(255, 255, 255, 0.8)',
                  },
                }}
              />
            ) : (
              <IconButton
                size="small"
                onClick={() => setSearchOpen(true)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <SearchIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
        {hasFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {postsData?.count ?? posts.length}件のポストが見つかりました
            </Typography>
            <Button
              size="small"
              onClick={handleClearFilter}
              startIcon={<Clear fontSize="small" />}
              sx={{ fontSize: '0.8rem', textTransform: 'none', p: '2px 8px' }}
            >
              Clear filter
            </Button>
          </Box>
        )}
      </Box>

      {/* ポスト一覧 */}
      {loading ? (
        <PostLoadingPlaceholder />
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {hasFilters ? '該当するポストがありません' : (isMyPage ? 'まだ投稿がありません' : 'ポストはまだありません')}
          </Typography>
        </Box>
      ) : (
        <>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              isMyPage={isMyPage}
              isProfile
              onDelete={handleDelete}
              onReactionUpdate={handleReactionUpdate}
            />
          ))}

          {/* ページネーション */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <Button
              onClick={() => handlePageChange(page - 1)}
              disabled={isFetching || page === 1}
              variant="outlined"
              size="small"
            >
              前のページ
            </Button>
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2
              }}
            >
              {page}ページ目
            </Typography>
            <Button
              onClick={() => handlePageChange(page + 1)}
              disabled={isFetching || !hasNext}
              variant="outlined"
              size="small"
            >
              次のページ
            </Button>
          </Box>
        </>
      )}

      {/* 期間選択ポップオーバー */}
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            期間指定
          </Typography>
          <TextField
            label="開始日"
            type="date"
            size="small"
            value={tempDateFrom}
            onChange={(e) => setTempDateFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
          />
          <TextField
            label="終了日"
            type="date"
            size="small"
            value={tempDateTo}
            onChange={(e) => setTempDateTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!tempDateFrom && !tempDateTo}
            onClick={() => {
              setDateFrom(tempDateFrom);
              setDateTo(tempDateTo);
              setDateAnchor(null);
              handlePageChange(1);
            }}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            適用
          </Button>
        </Box>
      </Popover>

      <GameSelectDialog
        open={gameDialogOpen}
        onClose={() => setGameDialogOpen(false)}
        onSelect={(game) => {
          setGameFilter({
            game_id: game.game_id,
            name: game.japanese_name || game.name,
          });
          setGameDialogOpen(false);
          handlePageChange(1);
        }}
        title="ゲームでフィルター"
      />
    </Box>
  );
};

export default PostFeed;
